import type { FloraDefinition } from '@/flora/FloraDefinition';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import { formFactorOf } from './FloraFacets';

/**
 * Palette thumbnails, drawn as inline SVG silhouettes from the plant's own seasonal palette.
 *
 * Rendering real 3D previews offscreen would cost a framebuffer and a render pass per catalog entry
 * for a 48px tile; a vector silhouette keyed to the same form archetype communicates habit and
 * colour just as well, updates instantly when the season changes, and stays crisp at any DPI.
 */

export interface ThumbnailOptions {
  season?: SimpleSeason;
  /** Draw the ground line and sky wash. Off for compact list rows. */
  chrome?: boolean;
}

/** Guard against a malformed catalog colour reaching the DOM as markup. */
export function safeColor(value: string | undefined, fallback: string): string {
  return value !== undefined && /^#[0-9a-fA-F]{3,8}$/.test(value) ? value : fallback;
}

function shade(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean.slice(0, 6);
  const channels = [0, 2, 4].map((i) => {
    const value = parseInt(full.slice(i, i + 2), 16);
    const shifted = amount >= 0 ? value + (255 - value) * amount : value * (1 + amount);
    return Math.round(Math.min(255, Math.max(0, shifted)))
      .toString(16)
      .padStart(2, '0');
  });
  return `#${channels.join('')}`;
}

interface Palette {
  foliage: string;
  foliageDark: string;
  foliageLight: string;
  trunk: string;
  accent: string | null;
  bare: boolean;
}

function paletteFor(definition: FloraDefinition, season: SimpleSeason): Palette {
  const appearance = definition.seasons[season];
  const foliage = safeColor(appearance.foliageColor, '#4c7a34');
  const accentSource = appearance.flowerColor ?? appearance.fruitColor ?? definition.seasons.bloomWindows?.[0]?.color;
  return {
    foliage,
    foliageDark: shade(foliage, -0.28),
    foliageLight: shade(foliage, 0.16),
    trunk: safeColor(definition.rendering.trunkColor, '#6b4b2f'),
    accent: accentSource !== undefined ? safeColor(accentSource, '#f0efdc') : null,
    bare: appearance.dormant || appearance.foliageDensity <= 0.05,
  };
}

function accentDots(
  palette: Palette,
  points: readonly [number, number][],
  radius: number,
): string {
  if (!palette.accent) return '';
  return points
    .map(([x, y]) => `<circle cx="${x}" cy="${y}" r="${radius}" fill="${palette.accent}"/>`)
    .join('');
}

/** Body of the silhouette, drawn in a 64×64 viewBox with the ground line at y = 56. */
function silhouette(definition: FloraDefinition, palette: Palette): string {
  const form = formFactorOf(definition);
  const { foliage, foliageDark, foliageLight, trunk } = palette;

  switch (form) {
    case 'tree': {
      const conifer = definition.rendering.geometryForm === 'conical-tree';
      if (conifer) {
        return (
          `<path d="M31 56V22h2v34z" fill="${trunk}"/>` +
          `<path d="M32 6l11 18H21z" fill="${foliageLight}"/>` +
          `<path d="M32 16l13 15H19z" fill="${foliage}"/>` +
          `<path d="M32 27l15 17H17z" fill="${foliageDark}"/>` +
          accentDots(palette, [[25, 33], [40, 38]], 2)
        );
      }
      const canopy = palette.bare
        ? `<path d="M32 40V18 M32 26l-9-8 M32 30l10-9 M32 20l-6-6 M32 22l7-7" stroke="${trunk}" stroke-width="2.4" stroke-linecap="round" fill="none"/>`
        : `<circle cx="22" cy="26" r="11" fill="${foliageDark}"/>` +
          `<circle cx="42" cy="27" r="10.5" fill="${foliage}"/>` +
          `<circle cx="32" cy="19" r="12.5" fill="${foliageLight}"/>` +
          `<circle cx="32" cy="28" r="11" fill="${foliage}"/>` +
          accentDots(palette, [[24, 18], [40, 20], [33, 33], [45, 30]], 2.1);
      return `<path d="M30 56V30h4v26z" fill="${trunk}"/>${canopy}`;
    }
    case 'shrub': {
      if (palette.bare) {
        return (
          `<path d="M32 56V34 M32 40l-9-6 M32 42l10-7 M32 36l-6-5 M32 34l7-5" ` +
          `stroke="${trunk}" stroke-width="2.2" stroke-linecap="round" fill="none"/>`
        );
      }
      return (
        `<path d="M32 56V40 M32 46l-7-6 M32 46l8-6" stroke="${trunk}" stroke-width="2" stroke-linecap="round" fill="none"/>` +
        `<ellipse cx="21" cy="42" rx="12" ry="10" fill="${foliageDark}"/>` +
        `<ellipse cx="43" cy="42" rx="12" ry="10" fill="${foliage}"/>` +
        `<ellipse cx="32" cy="34" rx="14" ry="12" fill="${foliageLight}"/>` +
        accentDots(palette, [[24, 29], [40, 30], [32, 24], [46, 38], [19, 39]], 2.6)
      );
    }
    case 'grass': {
      const blades = [
        'M32 56C31 46 27 34 20 26',
        'M32 56C32 44 31 32 28 20',
        'M32 56c0-12 2-24 6-34',
        'M32 56c1-10 5-22 12-28',
        'M32 56c-1-8-6-16-12-20',
        'M32 56c1-8 7-15 13-18',
      ]
        .map(
          (d, i) =>
            `<path d="${d}" stroke="${i % 2 === 0 ? foliage : foliageDark}" stroke-width="2.6" ` +
            `stroke-linecap="round" fill="none"/>`,
        )
        .join('');
      return blades + accentDots(palette, [[20, 25], [28, 19], [38, 21], [44, 27]], 2.2);
    }
    case 'perennial': {
      const stems = [
        [32, 32],
        [23, 36],
        [41, 35],
        [28, 28],
      ] as const;
      const stemPaths = stems
        .map(([x, y]) => `<path d="M32 56C32 48 ${x} 46 ${x} ${y}" stroke="${foliageDark}" stroke-width="2.2" fill="none"/>`)
        .join('');
      return (
        stemPaths +
        `<ellipse cx="32" cy="50" rx="14" ry="7" fill="${foliage}"/>` +
        (palette.accent
          ? stems.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="4.2" fill="${palette.accent}"/>`).join('') +
            stems.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="1.6" fill="${foliageDark}"/>`).join('')
          : stems.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="3.4" fill="${foliageLight}"/>`).join(''))
      );
    }
    case 'groundcover':
      return (
        `<ellipse cx="20" cy="50" rx="13" ry="6" fill="${foliageDark}"/>` +
        `<ellipse cx="44" cy="50" rx="13" ry="6" fill="${foliage}"/>` +
        `<ellipse cx="32" cy="46" rx="16" ry="7" fill="${foliageLight}"/>` +
        accentDots(palette, [[24, 43], [40, 44], [32, 40]], 2)
      );
  }
}

/**
 * Build the thumbnail for a plant as an SVG string, ready to assign to `innerHTML`. All interpolated
 * values are numbers or colours validated by {@link safeColor}.
 */
export function floraThumbnailSvg(
  definition: FloraDefinition,
  options: ThumbnailOptions = {},
): string {
  const season = options.season ?? 'summer';
  const palette = paletteFor(definition, season);
  const chrome = options.chrome ?? true;
  const ground = chrome
    ? `<ellipse cx="32" cy="57" rx="24" ry="3.5" fill="currentColor" opacity="0.14"/>`
    : '';
  return (
    `<svg class="lg-thumb__svg" viewBox="0 0 64 64" role="img" ` +
    `aria-label="${definition.botanicalName}">${ground}${silhouette(definition, palette)}</svg>`
  );
}

/** Ordered swatch strip for a plant's year-round colour story. */
export function seasonalSwatches(definition: FloraDefinition): { season: SimpleSeason; color: string }[] {
  return (['spring', 'summer', 'autumn', 'winter'] as const).map((season) => {
    const appearance = definition.seasons[season];
    const bare = appearance.dormant || appearance.foliageDensity <= 0.05;
    const flower = (appearance.flowerDensity ?? 0) > 0.4 ? appearance.flowerColor : undefined;
    return {
      season,
      color: safeColor(bare ? definition.rendering.trunkColor : flower ?? appearance.foliageColor, '#6b5335'),
    };
  });
}
